import { useState } from "react";
import DateTimePicker from "./DateTimePicker";
import Dialog from "./Dialog";

function MentorRequestModal({ isOpen, request, onClose, onAction }) {
  const [mode, setMode] = useState(null); // approve | reject | counter
  const [message, setMessage] = useState("");
  const [start, setStart] = useState(null);
  const [duration, setDuration] = useState(20);
  const [counterError, setCounterError] = useState("");

  if (!isOpen || !request) return null;

  const computedEnd = start
    ? new Date(start.getTime() + duration * 60000)
    : null;

  const submit = () => {
    if (mode === "counter" && !start) {
      setCounterError("Please select a start time for your proposed time.");
      return;
    }
    setCounterError("");

    let payload = { mentor_message: message };
    if (mode === "counter") {
      payload = {
        ...payload,
        proposed_start_time: start.toISOString(),
        proposed_end_time: new Date(start.getTime() + duration * 60000).toISOString(),
      };
    }
    onAction(mode, payload);
  };

  const fmt = (iso) =>
    new Date(iso).toLocaleString([], {
      weekday: "short", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

  const modeBtn = (value, label, activeClass) =>
    `flex-1 py-2 rounded-lg text-sm font-medium transition-colors border ${
      mode === value
        ? activeClass
        : "border-gray-300 text-gray-600 hover:bg-gray-50"
    }`;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
    >
      {({ titleId }) => (
        <div>
          <h3 id={titleId} className="mb-1 text-lg font-semibold text-gray-900">
            Session request
          </h3>
          <p className="mb-5 text-sm text-gray-500">
            Review and respond to this session request.
          </p>

          <div className="mb-5 rounded-xl border border-[#dde0f7] bg-[#eef0fa] px-4 py-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#282E6A]">Requested by student</p>
            <p className="text-sm font-medium text-[#0f1240]">{request.student_name}</p>
            <p className="mt-0.5 text-sm text-[#282E6A]">
              {fmt(request.requested_start_time)} – {new Date(request.requested_end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </p>
            {request.message && (
              <p className="mt-1.5 text-sm italic text-[#282E6A]">"{request.message}"</p>
            )}
          </div>

          <div className="mb-5 flex gap-2">
            <button
              onClick={() => { setMode("approve"); setCounterError(""); }}
              className={modeBtn("approve", "Approve", "border-green-500 bg-green-50 text-green-700")}
            >
              Approve
            </button>
            <button
              onClick={() => { setMode("reject"); setCounterError(""); }}
              className={modeBtn("reject", "Reject", "border-red-400 bg-red-50 text-red-700")}
            >
              Reject
            </button>
            <button
              onClick={() => {
                setMode("counter");
                setCounterError("");
                if (!start) setStart(new Date(request.requested_start_time));
              }}
              className={modeBtn("counter", "Counter", "border-[#282E6A] bg-[#eef0fa] text-[#282E6A]")}
            >
              Counter
            </button>
          </div>

          {mode === "counter" && (
            <div className="mb-5 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Proposed start time
                </label>
                <DateTimePicker
                  value={start}
                  onChange={(date) => { setStart(date); setCounterError(""); }}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Duration
                </label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm transition focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]"
                >
                  <option value={20}>20 minutes</option>
                  <option value={40}>40 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>

              {computedEnd && (
                <p className="text-xs text-gray-400">
                  Session ends at{" "}
                  <span className="font-medium text-gray-600">
                    {computedEnd.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </p>
              )}

              {counterError && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {counterError}
                </div>
              )}
            </div>
          )}

          <div className="mb-5">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Message to student <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              placeholder="Add a note for the student…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={submit}
              disabled={!mode}
              className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A] ${
                !mode
                  ? "cursor-not-allowed bg-gray-200 text-gray-400"
                  : mode === "approve"
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : mode === "reject"
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-[#282E6A] text-white hover:bg-[#1e2458]"
              }`}
              data-autofocus="true"
            >
              {!mode ? "Select an action" : mode === "approve" ? "Approve session" : mode === "reject" ? "Reject request" : "Send counter-offer"}
            </button>
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default MentorRequestModal;
